import {
  collapsibleIsOpen,
  toggleCollapsible
} from "../../Shared/Collapsible/collapsible";
import {
  getRequiredAttribute,
  getRequiredDatasetAttribute,
  getRequiredElement
} from "../../Shared/Utilities/attributes";
import { replacePlaceholders } from "../../Shared/Utilities/string";

type GroupedByAnimalType = Record<string, HTMLElement[]>;

/**
 * Initializes a pet letter section by setting up form validation and letter display logic.
 */
export const initializePetLetterSection = (petLetterSection: HTMLElement) => {
  const petLetterForm = getRequiredElement<HTMLFormElement>(
    petLetterSection,
    "[data-name='pet-letter-form']",
    "Pet Letter Form missing"
  );

  const petTypeRadioButtonSection = getRequiredElement<HTMLElement>(
    petLetterForm,
    "[data-name='pet-letter-animal-type-radios']",
    "Pet Radio Button Section missing"
  );

  const petTypeRadioButtons = getRequiredElement<HTMLElement>(
    petLetterForm,
    "[data-name='pet-letter-animal-type-radios']",
    "Pet RadioButton Section missing"
  ).querySelectorAll<HTMLInputElement>("[type='radio']");

  const radioFormDataName = getRequiredDatasetAttribute(
    petTypeRadioButtonSection,
    "formDataName",
    "Radio type name is missing"
  );

  const petNameField = getRequiredElement<HTMLInputElement>(
    petLetterForm,
    "[data-name='pet-letter-input-name']",
    "Pet Letter Name field missing"
  );

  const petAgeField = getRequiredElement<HTMLInputElement>(
    petLetterForm,
    "[data-name='pet-letter-input-age']",
    "Pet Letter Age field missing"
  );

  const submitButton = getRequiredElement<HTMLElement>(
    petLetterForm,
    "[data-name='pet-letter-submit']",
    "Pet Letter submit button missing"
  );

  const petLetterContentSections = petLetterSection.querySelectorAll<HTMLElement>(
    "[data-name='pet-letter-content']"
  );

  const petLetterCollapsible = getRequiredElement<HTMLElement>(
    petLetterSection,
    "#pet-letter-content",
    "Pet letter collapsible is missing"
  );

  const getAnotherLetterButton = getRequiredElement<HTMLElement>(
    petLetterSection,
    "[data-name='get-another-letter']",
    "Get Another Letter button missing"
  );

  getAnotherLetterButton.addEventListener("click", handleGetLetter);
  petLetterForm.addEventListener("submit", handleGetLetter);

  /**
   * Handles the form submission or manual request for another letter.
   */
  function handleGetLetter(event: Event) {
    event.preventDefault();
    if (validateForm()) {
      getLetter();
      getAnotherLetterButton.classList.remove("hidden");
    }
  }

  /**
   * Validates the form, showing validation messages where needed.
   * @returns {boolean} Whether the form is valid.
   */
  const validateForm = (): boolean => {
    if (petLetterForm.checkValidity()) {
      return true;
    }

    petLetterForm
      .querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-pet-letter-field]")
      .forEach(addValidationMessage);

    return false;
  };

  /**
   * Displays validation messages for invalid fields.
   */
  const addValidationMessage = (field: HTMLInputElement | HTMLSelectElement) => {
    const validationElement = getRequiredElement<HTMLElement>(
      petLetterForm,
      `[data-valmsg-for='${field.name}']`,
      "Validation area missing"
    );

    validationElement.innerHTML =
      field.dataset.customValidationMessage ?? field.validationMessage;
  };

  /**
   * Retrieves and displays a pet letter based on the selected animal type.
   */
  const getLetter = () => {
    const formData = Object.fromEntries(new FormData(petLetterForm));
    const animalType = formData[radioFormDataName] as string;

    const lettersGroupedByAnimalType = groupLettersByAnimalType(petLetterContentSections);
    const matchingLetters = lettersGroupedByAnimalType[animalType];

    if (!matchingLetters) {
      throw `No letters found for animal type: ${animalType}`;
    }

    updateLettersWithFormData(
      matchingLetters,
      formData[petNameField.name] as string,
      formData[petAgeField.name] as string
    );
    displayRandomLetter(matchingLetters);

    if (!collapsibleIsOpen(petLetterCollapsible)) {
      toggleCollapsible(petLetterCollapsible);
    }
  };

  let lastIndex = -1;

  /**
   * Displays a random letter while ensuring it's not the same as the last one.
   */
  const displayRandomLetter = (letters: HTMLElement[]) => {
    petLetterContentSections.forEach(letter => {
      letter.classList.replace("opacity-100", "opacity-0");
    });

    const { element: randomLetter, newLastIndex } = getRandomElement(letters, lastIndex);

    if (!randomLetter) {
      throw "Letter missing";
    }

    lastIndex = newLastIndex;
    randomLetter.classList.replace("opacity-0", "opacity-100");
  };

  /**
   * Updates all letters with the provided pet name and age.
   */
  const updateLettersWithFormData = (
    letters: HTMLElement[],
    name: string,
    age: string
  ) => {
    letters.forEach(letter => {
      const letterBody = getRequiredElement<HTMLElement>(
        letter,
        "[data-body]",
        "Letter body is missing"
      );
      const letterSignature = getRequiredElement<HTMLElement>(
        letter,
        "[data-signature]",
        "Letter signature is missing"
      );

      letterBody.innerHTML = replacePlaceholders(
        getRequiredAttribute(letterBody, "data-template", "Body template missing"),
        name,
        age
      );
      letterSignature.innerHTML = replacePlaceholders(
        getRequiredAttribute(
          letterSignature,
          "data-template",
          "Signature template missing"
        ),
        name
      );
    });
  };

  /**
   * Adds real-time validation error removal on input change.
   */
  petLetterForm
    .querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-pet-letter-field]")
    .forEach(field => {
      field.addEventListener("change", () => {
        const validationElement = petLetterForm.querySelector<HTMLElement>(
          `[data-valmsg-for='${field.name}']`
        );
        if (field.validity.valid && validationElement) {
          validationElement.innerHTML = "";
        }
      });
    });
};

/**
 * Selects a random element from an array, ensuring it isn't the same as the last selected.
 */
const getRandomElement = <T>(
  array: T[],
  lastIndex: number
): { element: T | null; newLastIndex: number } => {
  if (array.length === 0) {
    return { element: null, newLastIndex: -1 };
  }
  if (array.length === 1) {
    return { element: array[0], newLastIndex: 0 };
  }

  const possibleIndices = array.map((_, i) => i).filter(i => i !== lastIndex);
  const newLastIndex =
    possibleIndices[Math.floor(Math.random() * possibleIndices.length)];

  return { element: array[newLastIndex], newLastIndex };
};

/**
 * Groups pet letters by their associated animal type.
 */
const groupLettersByAnimalType = (
  sections: NodeListOf<HTMLElement>
): GroupedByAnimalType =>
  Array.from(sections).reduce<GroupedByAnimalType>((acc, element) => {
    const animalType = element.getAttribute("data-animal-type");
    if (!animalType) {
      throw `AnimalType does not exist on element ${element}`;
    }
    (acc[animalType] ||= []).push(element);
    return acc;
  }, {});

// Initialize all pet letter sections
document
  .querySelectorAll<HTMLElement>("[data-name='pet-letter-section']")
  .forEach(initializePetLetterSection);
